import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import { API_CONFIG } from '../../config/apiConfig';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const ContactForm: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
  });

  const handleInputChange = (field: keyof ContactFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Send email using the backend API through Kong
      const response = await fetch(`${API_CONFIG.USER_SERVICE_URL}/api/v1/users/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          ...formData,
          to: 'keith@prismaticforge.ai',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      setSuccess(true);
      // Reset form after 3 seconds
      setTimeout(() => {
        setSuccess(false);
        setFormData({
          name: user?.name || '',
          email: user?.email || '',
          subject: '',
          message: '',
        });
      }, 3000);
    } catch (err) {
      console.error('Error sending contact form:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.subject && formData.message;

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Contact Support
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        Have a question or need help? Send us a message and we'll respond to you at keith@prismaticforge.ai as soon as possible.
      </Typography>

      {success ? (
        <Alert severity="success" style={{ marginBottom: 24 }}>
          Message sent successfully! We'll get back to you soon.
        </Alert>
      ) : (
        <Paper variant="outlined" style={{ padding: 24 }}>
          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Your Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              margin="normal"
              required
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              margin="normal"
              required
              disabled={loading}
              helperText="We'll use this to respond to your inquiry"
            />

            <TextField
              fullWidth
              label="Subject"
              value={formData.subject}
              onChange={handleInputChange('subject')}
              margin="normal"
              required
              disabled={loading}
              placeholder="Brief description of your inquiry"
            />

            <TextField
              fullWidth
              label="Message"
              multiline
              rows={6}
              value={formData.message}
              onChange={handleInputChange('message')}
              margin="normal"
              required
              disabled={loading}
              placeholder="Please describe your question or issue in detail..."
            />

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!isFormValid || loading}
                startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
                size="large"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </Paper>
      )}
    </div>
  );
};